import { settingsManager } from './../../../shared/settings';
import { IAjaxCreationMethod } from '@microsoft/portal-app/lib/auth';
import { IAction } from '@microsoft/portal-app/lib/redux';
import {
  addAjaxEpic,
  getFailureActionType,
  getSuccessActionType,
} from '@microsoft/portal-app/lib/redux/createAjaxEpic';
import { getDevicesMSGraphUrl } from '../../../redux/modules/accountApis';
import { IDevice, IDeviceReturn } from '../../models/IDevice';
import { AnyMeta, AnyPayload } from '@microsoft/portal-app/lib/redux/AnyPayload';
import { errorHandler } from '@microsoft/portal-app/lib/redux/observableErrorHandler';
import { ActionsObservable, Epic } from 'redux-observable';
import { Observable } from 'rxjs/Observable';
import { ajax } from 'rxjs/observable/dom/ajax';
import { catchError, concat, map, mergeMap } from 'rxjs/operators';
import { getDevicesAADGraphApiUrl } from '../../../redux/modules/accountApis';
import { IBitlockerKey, volumeType } from '../../models';
import { IDevicesState, IRootDevicesState } from '../../models/IDevicesState';
import { DevicesActions } from '../DevicesActions';
import { devicesRegistry as registry } from '../devicesRegistry';
import { IDevicesAction } from '../IDevicesAction';
import { withAuth } from '@microsoft/portal-app/lib/auth';
import { IODataListValue } from '@microsoft/portal-app/lib/odata-utils';
import { FluxStandardAction } from 'flux-standard-action';

const withAuthAjax = withAuth(ajax);
export const getDevicesAADGraphEpic: Epic<IDevicesAction<AnyPayload>, IRootDevicesState> = (
  action$: ActionsObservable<IDevicesAction>,
): Observable<IDevicesAction> =>
  action$.ofType(DevicesActions.getDevicesAADGraph).pipe(
    mergeMap(() => getDevicesAADGraphAjax(true).pipe(concat(getDevicesAADGraphAjax(false)))),
    map((devices: Map<string, IDevice>) => {
      return {
        type: DevicesActions.getDevicesAADGraphResponse,
        payload: devices,
      };
    }),
    catchError(errorHandler<IDevicesAction>(DevicesActions.getDevicesAADGraphFailed)),
  );
registry.addEpic(DevicesActions.getDevicesAADGraph, getDevicesAADGraphEpic);

export const getDevicesAADGraphResponse = (
  state: IDevicesState,
  { payload: devicesById }: IDevicesAction<Map<string, IDevice>>,
): IDevicesState => {
  if (!devicesById) {
    return state;
  }
  const newDevicesById = convertDeviceData(devicesById);
  return {
    ...state,
    loading: false,
    loadingError: false,
    devicesById: newDevicesById,
  };
};
registry.add(DevicesActions.getDevicesAADGraphResponse, getDevicesAADGraphResponse);

export const getDevicesAADGraphFailed = (
  state: IDevicesState,
  _action: Partial<FluxStandardAction<AnyPayload, AnyMeta>>,
) => {
  return {
    ...state,
    loading: false,
    loadingError: true,
  };
};
registry.add(DevicesActions.getDevicesAADGraphFailed, getDevicesAADGraphFailed);

const getDevicesAADGraphAjax = (allowCache: boolean) =>
  withAuthAjax
    .postJSON<IODataListValue<IDevice[]>, undefined>({
      url: getDevicesAADGraphApiUrl(),
      headers: {
        ...(allowCache ? {} : { 'Cache-Control': 'no-cache' }),
      },
    })
    .pipe(
      map((response) => response.value),
      map((devices) =>
        devices.reduce((acc, device) => {
          let bitlockerKeyAvailable = false;
          const bitlockerKeys: IBitlockerKey[] = [];
          if (device.bitLockerKey && device.bitLockerKey.length > 0) {
            bitlockerKeyAvailable = true;
            // If this getDevice API is being called we shouldn't be calling MS Graph for bitlocker.
            // But I'm leaving this in just in case there is an issue with the ms graph get device call rollout.
            const callMSGraphForBitlocker = settingsManager.get('getDeviceAndBitlockerMsGraphCallEnabled');
            if (!callMSGraphForBitlocker) {
              device.bitLockerKey.forEach((key: IBitlockerKey) => {
                const bitlockerKey = convertBitlockerDataType(key);
                bitlockerKeys.push(bitlockerKey);
              });
            }
          }
          device = {
            ...device,
            bitLockerKey: bitlockerKeys,
            bitlockerKeyAvailable,
          };
          acc.set(device.deviceId, device);
          return acc;
        }, new Map<string, IDevice>()),
      ),
    );

export const getDevicesMSGraph = (ajax: IAjaxCreationMethod, _action: IAction<string>) => {
  return ajax.getJSON<IDevice[]>(getDevicesMSGraphUrl());
};
addAjaxEpic(registry, DevicesActions.getDevices, getDevicesMSGraph);

const getDevicesMSGraphSuccess = (state: IDevicesState, action: IDevicesAction<IDeviceReturn>): IDevicesState => {
  if (action === undefined || action.payload === undefined) {
    return state;
  }
  const devices = action.payload.value;
  const devicesById: Map<string, IDevice> = new Map<string, IDevice>();
  devices.forEach((device) => {
    devicesById.set(device.deviceId, device);
  });
  if (!devicesById) {
    return state;
  }
  return {
    ...state,
    loading: false,
    loadingError: false,
    devicesById,
  };
};
registry.add(getSuccessActionType(DevicesActions.getDevices), getDevicesMSGraphSuccess);

export const getDevicesMSGraphFailure = (
  state: IDevicesState,
  action: Partial<FluxStandardAction<AnyPayload, AnyMeta>>,
): IDevicesState => {
  return {
    ...state,
    loading: false,
    loadingError: true,
    error: action.payload,
  };
};
registry.add(getFailureActionType(DevicesActions.getDevices), getDevicesMSGraphFailure);

// delete when we move to MS Graph fully. The only thing currently stopping us is the new bitlocker API not being in national clouds.
const convertBitlockerDataType = (oldKey: IBitlockerKey): IBitlockerKey => {
  let volumeTypeConverted: volumeType;
  switch (oldKey.driveTypeIndicator) {
    case 0:
      volumeTypeConverted = volumeType.unknownFutureValue;
      break;
    case 1:
      volumeTypeConverted = volumeType.operatingSystemVolume;
      break;
    case 2:
      volumeTypeConverted = volumeType.fixedDataVolume;
      break;
    case 3:
      volumeTypeConverted = volumeType.removableDataVolume;
      break;
    default:
      volumeTypeConverted = volumeType.unknownFutureValue;
      break;
  }
  const newBitlockerKey: IBitlockerKey = {
    id: oldKey.keyIdentifier || '',
    createdDateTime: oldKey.creationTime || '',
    volumeType: volumeTypeConverted,
    deviceId: '',
    recoveryKey: oldKey.recoveryKey,
  };
  return newBitlockerKey;
};

const convertDeviceData = (deviceListOld: Map<string, IDevice>): Map<string, IDevice> => {
  let deviceListNew: Map<string, IDevice> = new Map<string, IDevice>();

  deviceListOld.forEach((oldDevice: IDevice, key: string) => {
    const newDevice: IDevice = {
      ...oldDevice,
      id: oldDevice.objectId || '',
      operatingSystem: oldDevice.deviceOSType,
      operatingSystemVersion: oldDevice.deviceOSVersion,
      trustType: oldDevice.deviceTrustType,
      approximateLastSignInDateTime: oldDevice.approximateLastLogonTimeStamp,
    };
    deviceListNew.set(key, newDevice);
  });

  return deviceListNew;
};
